import { fromMGRS, geocoding, toMGRS } from '@m1/geo-utils';
import OlMap from '@m1/ol-map';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { RiSearchLine, RiCheckLine } from 'react-icons/ri';
import { useSelector, useStore } from 'react-redux';

import styles from './Geo.module.scss';
import Term from './Term';

import Bar from '@/components/ui/Bar';
import { IconButton } from '@/components/ui/kit/Button';
import { FieldText } from '@/components/ui/kit/Field';
import * as REGEX from '@/define/regExp';
import VWorldMap from '@/helpers/VWorldMap';
import { NotifierService as notifier } from '@/libs/Notifier';

const cx = classNames.bind(styles);

const FORM = {
  MGRS: Symbol('MGRS'),
  COORDINATES: Symbol('COORDINATES'),
  ADDRESS: Symbol('ADDRESS'),
};

const Geo = () => {
  const store = useStore();
  const center = useSelector((state) => state.map.center);
  const position = useSelector((state) => state.map.position);
  const pointer = useSelector((state) => state.map.pointer);
  const focusRobotId = useSelector((state) => state.robot?.focusRobotId);
  const [mgrs, setMgrs] = useState('');
  const [activeForm, setActiveForm] = useState();
  const mgrsTermRef = useRef();
  const mgrsRef = useRef();
  const coordinatesTermRef = useRef();
  const coordinatesRef = useRef();
  const addressTermRef = useRef();
  const addressRef = useRef();

  const termRefs = {
    [FORM.MGRS]: mgrsTermRef,
    [FORM.COORDINATES]: coordinatesTermRef,
    [FORM.ADDRESS]: addressTermRef,
  };
  const fieldRefs = {
    [FORM.MGRS]: mgrsRef,
    [FORM.COORDINATES]: coordinatesRef,
    [FORM.ADDRESS]: addressRef,
  };

  useEffect(() => {
    coordinatesRef.current.value = `${center.lat.toFixed(7)}, ${center.lng.toFixed(7)}`;
  }, [center]);

  useUpdateEffect(() => {
    const nextMGRS = toMGRS(position);
    mgrsRef.current.value = nextMGRS;
    setMgrs(nextMGRS);
  }, [position]);

  useUpdateEffect(() => {
    if (focusRobotId) {
      setActiveForm();
    }
  }, [focusRobotId]);

  useLayoutEffect(() => {
    if (activeForm) {
      adjustPanelPosition();
      fieldRefs[activeForm].current.focus();
      document.addEventListener('mousedown', unfocusPanel);
    }

    return () => {
      document.removeEventListener('mousedown', unfocusPanel);
    };
  }, [activeForm]);

  const unfocusPanel = (e) => {
    if (mgrsTermRef.current.contains(e.target)) return;
    if (coordinatesTermRef.current.contains(e.target)) return;
    if (addressTermRef.current.contains(e.target)) return;

    setActiveForm();
  };

  const toggleForm = (e, key) => {
    // Form 클릭된 경우 이벤트 방지
    const closest = e.target.closest('[data-form]');
    if (closest) return;

    // Focus 설정된 경우 이벤트 방지
    if (focusRobotId) {
      notifier.error('Please disable the robot focus.');
      return;
    }

    activeForm === key ? setActiveForm() : setActiveForm(key);
  };

  const changeMGRS = () => {
    if (mgrsRef.current.value.trim() === '') {
      notifier.error('Please enter the MGRS.');
      return;
    }

    try {
      const { lat, lng } = fromMGRS(mgrsRef.current.value);
      moveMap(lat, lng);
    } catch (e) {
      notifier.error('Please enter a value in MGRS format.');
    }
  };

  const changeCoordinates = () => {
    const [lat, lng] = coordinatesRef.current.value.split(', ');

    if (!REGEX.LATITUDE.test(lat) || !REGEX.LONGITUDE.test(lng)) {
      notifier.error('Please check the latitude and longitude.');
      return;
    }
    moveMap(Number(lat), Number(lng));
  };

  const searchAddress = async () => {
    if (addressRef.current.value.trim() === '') {
      notifier.error('Please enter the address.');
      return;
    }

    try {
      const { lat, lng } = await geocoding({ address: addressRef.current.value });
      moveMap(lat, lng);
    } catch (e) {
      notifier.error(e.message);
    }

    addressRef.current.value = '';
  };

  const moveMap = (lat, lng) => {
    if (store.getState().map3d.isShow) {
      VWorldMap.setCenter({ lat, lng }, { duration: 300 });
    } else {
      OlMap.setCenter({ lat, lng }, { duration: 300 });
    }
  };

  const pressKey = (e) => {
    if (e.code === 'Enter') {
      switch (e.target.name) {
        case 'mgrs':
          changeMGRS();
          break;

        case 'coordinates':
          changeCoordinates();
          break;

        case 'address':
          searchAddress();
          break;

        default:
          break;
      }
      setActiveForm();
    } else if (e.code === 'Escape') {
      setActiveForm();
    }
  };

  const confirm = (action) => {
    action();
    setActiveForm();
  };

  const adjustPanelPosition = () => {
    const targetTerm = termRefs[activeForm].current;
    // term 요소 좌측 위치
    const { left: termLeft } = targetTerm.getBoundingClientRect();

    const panel = targetTerm.querySelector('[data-form]');
    // panel 가로 크기
    const { width: panelWidth } = panel.getBoundingClientRect();

    // 부족한 공간 크기
    let shortfall = termLeft + panelWidth - window.innerWidth;
    shortfall = Math.max(shortfall, 0);

    // 부족한 공간 크기 만큼 좌측 이동
    panel.style.setProperty('left', `-${shortfall}px`);
  };

  return (
    <div className={cx('container')}>
      <div className={cx('mgrsSection')} data-hide-priority={2}>
        <Term ref={mgrsTermRef} label="MGRS" value={mgrs} onClick={(e) => toggleForm(e, FORM.MGRS)}>
          <div data-form className={cx(['panel', { show: activeForm === FORM.MGRS }])}>
            <FieldText ref={mgrsRef} name="mgrs" placeholder="MGRS" onKeyDown={pressKey} className={cx('field')} />
            <IconButton onClick={() => confirm(changeMGRS)} Icon={RiCheckLine} />
          </div>
        </Term>
        <Bar size={28} />
      </div>
      <Term
        ref={coordinatesTermRef}
        label="Center"
        value={`${center.lat.toFixed(7)}, ${center.lng.toFixed(7)}`}
        onClick={(e) => toggleForm(e, FORM.COORDINATES)}>
        <div data-form className={cx(['panel', { show: activeForm === FORM.COORDINATES }])}>
          <FieldText
            ref={coordinatesRef}
            name="coordinates"
            placeholder="Latitude, Longitude"
            onKeyDown={pressKey}
            className={cx('field')}
          />
          <IconButton onClick={() => confirm(changeCoordinates)} Icon={RiCheckLine} />
        </div>
      </Term>
      <Bar size={28} />
      <Term label="Pointer" value={pointer && `${pointer.lat.toFixed(7)}, ${pointer.lng.toFixed(7)}`} />
      <Bar size={28} />
      <div ref={addressTermRef} className={cx('addressTerm')} onClick={(e) => toggleForm(e, FORM.ADDRESS)}>
        <RiSearchLine size={18} color="white" title="Address" />
        <div data-form className={cx(['panel', { show: activeForm === FORM.ADDRESS }])}>
          <FieldText
            ref={addressRef}
            name="address"
            placeholder="Address Keyword"
            onKeyDown={pressKey}
            className={cx('field')}
          />
          <IconButton onClick={() => confirm(searchAddress)} Icon={RiCheckLine} />
        </div>
      </div>
    </div>
  );
};

export default Geo;
